import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import {
  ApartmentOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SubnodeOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import { Button, Empty, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { useCayPhongBan } from '../hooks/usePhongBan';
import { useThaoTacPhongBan } from '../hooks/useThaoTacPhongBan';
import { demNut, dungCay, idCoCon, locCay, themConDuoc, xoaDuoc } from '../nghiep-vu/cay';
import type { NutCay } from '../nghiep-vu/cay';

/** Thụt lề mỗi cấp của bảng cây (px). */
const THUT_LE = 20;
/** Bề rộng tên ở cấp 1; cấp sâu hơn trừ đi phần thụt lề. */
const RONG_TEN = '22rem';

/**
 * Cơ cấu tổ chức (cổng tenant, `/he-thong/to-chuc`, G2-D1-17; ADR-0014,
 * ADR-0067): cây phòng ban của tổ chức — thêm ở gốc hoặc dưới một phòng ban,
 * đổi tên, chuyển sang phòng ban cha khác, xoá phòng ban đã trống. Chỉ quản
 * trị tổ chức vào được (phân hệ `ORG_STRUCTURE`, dịch vụ `cau-hinh-he-thong`).
 * Gán người dùng vào phòng ban ở màn Cấu hình người dùng.
 *
 * Tải cả cây một lần (vài chục tới vài trăm phòng ban) rồi tìm ở client. Cây
 * mở rộng hết theo mặc định; nút người dùng thu gọn được nhớ trong phiên xem.
 */
export function CoCauToChucView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const [tuKhoa, setTuKhoa] = useState('');
  const [thuGon, setThuGon] = useState<ReadonlySet<string>>(new Set());
  const q = useCayPhongBan();
  const ds = useMemo(() => q.data ?? [], [q.data]);
  const thaoTac = useThaoTacPhongBan(ds);
  const tieuDe = t('nav.muc.coCauToChuc');

  const cay = useMemo(() => dungCay(ds, locale), [ds, locale]);
  const hien = useMemo(() => locCay(cay, tuKhoa), [cay, tuKhoa]);
  const coCon = useMemo(() => idCoCon(hien), [hien]);
  const moRong = coCon.filter((id) => !thuGon.has(id));
  const coLoc = tuKhoa.trim() !== '';
  const dangTai = q.isLoading;

  const doiMoRong = (id: string, mo: boolean) =>
    setThuGon((cu) => {
      const moi = new Set(cu);
      if (mo) moi.delete(id);
      else moi.add(id);
      return moi;
    });

  const columns: TableProps<NutCay>['columns'] = [
    {
      title: t('coCauToChuc.cotPhongBan'),
      width: 360,
      // Thụt lề và nút mở rộng của antd đứng cùng dòng với tên: không cho xuống
      // dòng (tên dài rơi xuống dưới là mất thụt lề), và trừ phần thụt lề khỏi
      // bề rộng tên để cột rộng như nhau ở mọi cấp, tên dài cắt bằng "…".
      onCell: () => ({ className: 'whitespace-nowrap' }),
      render: (_v, row) => (
        <span
          className="inline-flex min-w-0 flex-col align-middle"
          style={{ maxWidth: `calc(${RONG_TEN} - ${(row.cap - 1) * THUT_LE}px)` }}
        >
          <span className="truncate text-sm font-medium text-content-primary" title={row.name}>
            {row.name}
          </span>
          <span className="text-2xs text-content-muted">{t('coCauToChuc.cap', { so: row.cap })}</span>
        </span>
      ),
    },
    {
      title: t('coCauToChuc.cotNguoiDung'),
      dataIndex: 'user_count',
      width: 120,
      align: 'right',
      render: (value: number) =>
        value > 0 ? (
          <span className="text-sm tabular-nums text-content-primary">{soVN(value, locale)}</span>
        ) : (
          <span className="text-xs text-content-muted">{t('coCauToChuc.chuaCoAi')}</span>
        ),
    },
    {
      title: t('coCauToChuc.cotPhongCon'),
      dataIndex: 'child_count',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value > 0 ? (
          <span className="text-sm tabular-nums text-content-primary">{soVN(value, locale)}</span>
        ) : (
          <span className="text-xs text-content-muted">{t('chung.khongCo')}</span>
        ),
    },
    {
      title: t('coCauToChuc.cotTaoLuc'),
      dataIndex: 'created_at',
      width: 120,
      render: (value: string) => <span className="whitespace-nowrap text-xs text-content-muted">{ngayVN(value)}</span>,
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={row.name}
          muc={[
            {
              khoa: 'them-con',
              nhan: t('coCauToChuc.themPhongBanCon'),
              icon: <SubnodeOutlined />,
              hien: themConDuoc(row.cap),
              onChon: () => thaoTac.moTaoCon(row),
            },
            {
              khoa: 'sua',
              nhan: t('coCauToChuc.suaPhongBan'),
              icon: <EditOutlined />,
              onChon: () => thaoTac.moSua(row),
            },
            {
              khoa: 'xoa',
              nhan: t('coCauToChuc.xoaPhongBan'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              // Người có quyền nhưng phòng ban chưa trống: hiện kèm lý do (ADR-UI-0005).
              voHieu: !xoaDuoc(row),
              moTa: xoaDuoc(row) ? undefined : t('coCauToChuc.chuaXoaMoTa'),
              onChon: () => thaoTac.moXoa(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.cauHinhHeThong') },
            { label: t('nav.muc.cauHinhHeThong') },
            { label: tieuDe },
          ]}
          titleIcon={<ApartmentOutlined className="text-primary-600" />}
          title={tieuDe}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void q.refetch()}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={thaoTac.moTao}>
                {t('coCauToChuc.themPhongBan')}
              </Button>
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            allowClear
            className="w-full sm:w-80"
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('coCauToChuc.timPlaceholder')}
            aria-label={t('coCauToChuc.timNhan')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          {coCon.length > 0 && (
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              <Button variant="outline" size="sm" onClick={() => setThuGon(new Set())}>
                {t('coCauToChuc.moRongHet')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setThuGon(new Set(idCoCon(cay)))}>
                {t('coCauToChuc.thuGonHet')}
              </Button>
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {q.error && !q.data ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">
                {t('coCauToChuc.loiDanhSach', { loi: dichLoi(q.error) })}
              </span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<NutCay>
                caption={t('coCauToChuc.bangCaption')}
                rowKey="id"
                dataSource={hien}
                columns={columns}
                loading={dangTai}
                size="middle"
                pagination={false}
                expandable={{
                  expandedRowKeys: moRong,
                  onExpand: (mo, row) => doiMoRong(row.id, mo),
                  indentSize: THUT_LE,
                }}
                locale={{
                  emptyText: dangTai ? (
                    ' '
                  ) : (
                    <Empty
                      icon={ApartmentOutlined}
                      // Rỗng vì ô tìm: bỏ mô tả và lời mời hành động.
                      title={coLoc ? t('coCauToChuc.rongLoc', { tuKhoa: tuKhoa.trim() }) : t('coCauToChuc.rongThat')}
                      description={coLoc ? undefined : t('coCauToChuc.rongThatMo')}
                      action={
                        coLoc ? undefined : (
                          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={thaoTac.moTao}>
                            {t('coCauToChuc.themPhongBanDauTien')}
                          </Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {hien.length > 0 && (
                <div className="border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted" role="status">
                    {coLoc
                      ? t('coCauToChuc.dangHienLoc', {
                          so: soVN(demNut(hien), locale),
                          tong: soVN(ds.length, locale),
                        })
                      : t('coCauToChuc.dangHien', { so: soVN(ds.length, locale) })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {thaoTac.hopThoai}
    </div>
  );
}
